import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'
import media from '../../../utils/style'
import { Image } from 'rebass'
import theme from '../../../theme'

const SLIDE_SPACING = '24px'
const SLIDE_SPACING_MOBILE = '12px'

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  background: ${theme.colors.blue_light};
  padding: 16px 0;
`
const Carousel = styled.div`
  position: relative;
  overflow: hidden;
  max-width: 2560px;
  margin-left: auto;
  margin-right: auto;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 10%;
    height: 100%;
    background: linear-gradient(
      to right,
      ${theme.colors.blue_light},
      transparent
    );
    z-index: 1;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 10%;
    height: 100%;
    background: linear-gradient(
      to left,
      ${theme.colors.blue_light},
      transparent
    );
    z-index: 1;
  }
`

const CarouselContainer = styled.div`
  display: flex;
  touch-action: pan-y;
  backface-visibility: hidden;
  margin-left: calc(${SLIDE_SPACING_MOBILE} * -1);

  ${media.GiantScreenUp`
    margin-left: calc(${SLIDE_SPACING} * -1);
  `}
`

const CarouselSlide = styled.a`
  display: block;
  flex: 0 0 33.333%;
  min-width: 300px;
  padding-left: ${SLIDE_SPACING_MOBILE};
  display: flex;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  aspect-ratio: 117 / 32;
  user-select: none;

  flex: 0 0 calc(${SLIDE_SPACING_MOBILE} * 2 + 80%);

  img {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 12px;
  }

  ${media.GiantScreenUp`
    padding-left: ${SLIDE_SPACING};
    flex: 0 0 min(calc(${SLIDE_SPACING} + 1170px), 100vw);
  `}

  &[data-active='true'] {
    img {
      transform: scale(1);
    }
  }

  &:hover,
  &:focus {
    img {
      opacity: 0.9;
    }
  }

  &[data-previous='true'] {
    img {
      transform-origin: top right;
    }
  }

  &[data-next='true'] {
    img {
      transform-origin: top left;
    }
  }

  img {
    transition: all 0.3s ease-in-out;
    transform: scale(0.9);
    transform-origin: top center;
  }
`

const OfferCarouselLarge = ({ initialIndex = 0, items = [] }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: 'trimSnaps',
    loop: true,
    align: 'center',
    startIndex: initialIndex,
    skipSnaps: true
  })

  const onSelect = useCallback(() => {
    if (emblaApi) {
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }
  }, [emblaApi])

  React.useEffect(() => {
    if (emblaApi) {
      onSelect()
      emblaApi.on('select', onSelect)
      emblaApi.on('reInit', onSelect)
      return () => emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <CarouselWrapper>
      <Carousel ref={emblaRef}>
        <CarouselContainer>
          {items.map((item, index) => {
            const totalSlides = items.length
            const previousIndex =
              selectedIndex === 0 ? totalSlides - 1 : selectedIndex - 1
            const nextIndex =
              selectedIndex === totalSlides - 1 ? 0 : selectedIndex + 1

            return (
              <CarouselSlide
                key={item.title + index}
                href={item.link}
                data-active={index === selectedIndex}
                data-previous={index === previousIndex}
                data-next={index === nextIndex}
              >
                <Image src={item.src} alt={item.title} />
              </CarouselSlide>
            )
          })}
        </CarouselContainer>
      </Carousel>
    </CarouselWrapper>
  )
}

export default OfferCarouselLarge
